/**
 * Break-glass for the Aithora team (src/aithora/v3/team-access-routes.ts, decision 4). The team
 * reads no resident clinical data by default: a refused read answers 403 with
 * `code: 'TEAM_ACCESS_REQUIRED'`, and the console shows the request button on that message.
 */
import { route } from './route';
import { AITHORA_PREFIX } from './prefix';

const A = AITHORA_PREFIX;

export const TEAM_ACCESS_REQUIRED = 'TEAM_ACCESS_REQUIRED';

export type TeamAccessReasonCode = 'support_ticket' | 'safety_incident' | 'data_correction' | 'legal_compliance';

export const TEAM_ACCESS_REASONS: ReadonlyArray<{ code: TeamAccessReasonCode; label: string }> = [
  { code: 'support_ticket', label: 'Support ticket' },
  { code: 'safety_incident', label: 'Safety incident' },
  { code: 'data_correction', label: 'Data correction requested by the facility' },
  { code: 'legal_compliance', label: 'Legal or compliance' },
];

export interface TeamAccessRequest {
  id: string;
  requesterUserId: string;
  requesterName: string | null;
  organizationId: string;
  residentId: string | null;
  reasonCode: TeamAccessReasonCode;
  reason: string;
  ticket: string | null;
  details: string | null;
  hours: number;
  emergency: boolean;
  status: 'pending' | 'approved' | 'denied' | 'expired' | 'ended' | 'withdrawn';
  requestedAt: string | null;
  answerBy: string | null;
  decidedAt: string | null;
  startsAt: string | null;
  endsAt: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
}

export interface TeamAccessActive {
  id: string;
  name: string | null;
  reason: string;
  ticket: string | null;
  endsAt: string;
  emergency: boolean;
  residentId: string | null;
  readOnly: true;
}

/** POST /team-access/requests: a team member, with a fresh step-up. */
export const teamAccessRequest = route<{ real: true; request: TeamAccessRequest; noticesSent: number }, void, {
  organizationId: string; residentId?: string | null; reasonCode: TeamAccessReasonCode; ticket?: string; details: string;
  hours?: number; emergency?: boolean; stepUpToken?: string; password?: string; code?: string;
}>()('POST', `${A}/team-access/requests`, { honest: true });

/** GET /team-access/requests: mine (the team) or my building's (a Facility Admin). */
export const teamAccessRequests = route<{ real: true; requests: TeamAccessRequest[] }>()('GET', `${A}/team-access/requests`, { honest: true });

/** POST /team-access/requests/:id/approve | deny: the building's Facility Admin. */
export const teamAccessApprove = route<{ real: true; request: TeamAccessRequest }>()('POST', `${A}/team-access/requests/:id/approve`, { honest: true });
export const teamAccessDeny = route<{ real: true; request: TeamAccessRequest }>()('POST', `${A}/team-access/requests/:id/deny`, { honest: true });
/** POST /team-access/requests/:id/end: the requester or the Facility Admin. */
export const teamAccessEnd = route<{ real: true; request: TeamAccessRequest }>()('POST', `${A}/team-access/requests/:id/end`, { honest: true });
/** POST /team-access/requests/:id/review {note?}: the Facility Admin, after an emergency start. */
export const teamAccessReview = route<{ real: true; request: TeamAccessRequest }, void, { note?: string }>()('POST', `${A}/team-access/requests/:id/review`, { honest: true });

/** GET /team-access/active?organizationId=&residentId=: the banner. */
export const teamAccessActive = route<{ real: true; active: TeamAccessActive | null }, { organizationId: string; residentId?: string }>()(
  'GET', `${A}/team-access/active`, { honest: true });

/** GET /team-access/log?organizationId=: every break-glass view of the building's residents. */
export const teamAccessLog = route<{ real: true; views: Array<{ at: string; by: string; resource: string; residentId: string | null; route: string | null; ticket: string | null; reasonCode: string | null; emergency: boolean; breakGlass: true }> }, { organizationId?: string }>()(
  'GET', `${A}/team-access/log`, { honest: true });
